import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api, { formatApiError, saveToken, clearToken } from "@/lib/api";

const AuthCtx = createContext(null);

/**
 * AuthProvider — auth strettamente via cookie httpOnly `access_token`
 * (il fallback Bearer/localStorage è stato rimosso in SEC-004).
 * Espone anche `maintenanceMode` letto dall'endpoint pubblico `/config/public`.
 */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=loading, false=guest, object=user
  const [loading, setLoading] = useState(true);
  const [maintenanceMode, setMaintenanceMode] = useState(false);

  useEffect(() => {
    let cancelled = false;
    // Config pubblica: sempre disponibile, no auth
    api.get("/config/public")
      .then((r) => { if (!cancelled) setMaintenanceMode(Boolean(r.data?.maintenance_mode)); })
      .catch(() => { /* se fallisce lasciamo maintenanceMode=false */ });
    // Sessione utente
    api.get("/auth/me")
      .then((r) => { if (!cancelled) setUser(r.data); })
      .catch(() => { if (!cancelled) { setUser(false); clearToken(); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const login = useCallback(async (email, password, totp_code) => {
    const { data } = await api.post("/auth/login", { email, password, totp_code });
    if (data.token) saveToken(data.token);
    setUser(data.user);
    return data.user;
  }, []);

  const register = useCallback(async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    if (data.token) saveToken(data.token);
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      console.warn("Logout API error:", formatApiError(err));
    }
    clearToken();
    setUser(false);
  }, []);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch { return null; }
  }, []);

  const value = useMemo(
    () => ({ user, loading, maintenanceMode, login, register, logout, refresh, setUser, formatApiError }),
    [user, loading, maintenanceMode, login, register, logout, refresh],
  );

  return (
    <AuthCtx.Provider value={value}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
